import { UserRole } from '@ferty9/constants';

export const PERMISSIONS = [
  'admin_app.access',
  'dashboard.read',
  'dashboard.export',
  'clinical_dashboard.read',
  'clinical_dashboard.export',
  'doctor_report.read',
  'doctor_report.export',
  'daily_clinical_report.read',
  'daily_clinical_report.export',
  'feedback_tracker.read',
  'feedback_tracker.manual',
  'feedback_tracker.send_message',
  'feedback_tracker.export',
  'feedback_tracker.assign',
  'feedback_tracker.callback',
  'feedback_tracker.escalate',
  'feedback_tracker.audit',
  'feedback_tracker.manage',
  'offer_campaigns.view',
  'offer_campaigns.enroll',
  'offer_campaigns.display_board',
  'offer_campaigns.display_monitor',
  'offer_campaigns.analytics_email.view',
  'offer_campaigns.analytics_email.manage',
  'offer_campaigns.analytics_email.send',
  'feedback_responses.read',
  'feedback_responses.export',
  'feedback_responses.delete',
  'question_wise_response.read',
  'question_wise_response.export',
  'monthly_summary.read',
  'monthly_summary.export',
  'detailed_summary.read',
  'detailed_summary.export',
  'staff_management.read',
  'staff_management.create',
  'staff_management.update',
  'staff_management.delete',
  'user_accounts.read',
  'user_accounts.create',
  'user_accounts.update',
  'user_accounts.delete',
  'role_management.read',
  'role_management.manage',
  'system_settings.read',
  'system_settings.update',
  'integration_settings.read',
  'integration_settings.update',
  'integration_settings.rotate_secret',
  'integration_settings.test_connection',
  'general_settings.read',
  'general_settings.update',
  'general_settings.manage_workflows',
  'general_settings.audit',
  'patient_notifications.read',
  'patient_notifications.retry',
  'patient_notifications.cancel',
  'patient_notifications.export',
  'automation_data_sources.read',
  'automation_data_sources.manage',
  'communication_media_assets.read',
  'communication_media_assets.manage',
  'survey_questions.read',
  'survey_questions.create',
  'survey_questions.update',
  'survey_questions.delete',
  'survey_app.use',
  'exports.read',
  'exports.download',
  'business_reports.view',
  'business_reports.manage',
  'business_reports.import',
  'business_reports.manual_revenue.view',
  'business_reports.manual_revenue.manage',
  'business_reports.approve_manual_entries',
  'business_reports.validation.view',
  'business_reports.validation.decide',
  'business_reports.targets.view',
  'business_reports.targets.create',
  'business_reports.targets.update',
  'business_reports.targets.export',
  'business_reports.target_governance.manage',
  'business_reports.ownership_governance.manage',
  'business_reports.landing_override.request',
  'business_reports.landing_override.approve',
  'business_reports.landing_override.clear',
  'business_reports.export',
  'business_reports.waterfall.view',
  'business_reports.waterfall.export',
  'business_reports.waterfall.source_sensitive.view',
  'business_reports.waterfall.source_sensitive.export',
  'business_reports.daily_revenue.view',
  'business_reports.medics_revenue.view',
  'business_reports.reconciliation.view',
  'business_reports.operations.view',
  'business_reports.operations.retry',
  'business_reports.integrity.view',
  'business_reports.retention.execute',
  'business_reports.source_sensitive.view',
  'business_reports.source_sensitive.export',
  'module_management.view',
  'module_management.configure',
  'module_management.stage',
  'module_management.install',
  'module_management.approve',
  'module_management.enable',
  'module_management.disable',
  'module_management.upgrade',
  'module_management.rollback',
  'module_management.uninstall',
  'module_management.audit',
  'module_management.health',
  'module_management.sdk_download',
  'module_management.package_import',
  'module_management.publisher_manage',
  'module_management.publisher_approve',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const ADMIN_EXCLUDED_PERMISSIONS = new Set<Permission>([
  'module_management.publisher_manage',
  'module_management.publisher_approve',
  'module_management.sdk_download',
  'module_management.package_import',
  'module_management.view',
  'module_management.configure',
  'module_management.stage',
  'module_management.install',
  'module_management.approve',
  'module_management.enable',
  'module_management.disable',
  'module_management.upgrade',
  'module_management.rollback',
  'module_management.uninstall',
  'module_management.audit',
  'module_management.health',
  'role_management.manage',
  'system_settings.update',
  'integration_settings.update',
  'integration_settings.rotate_secret',
  'integration_settings.test_connection',
  'business_reports.view',
  'business_reports.manage',
  'business_reports.import',
  'business_reports.manual_revenue.view',
  'business_reports.manual_revenue.manage',
  'business_reports.approve_manual_entries',
  'business_reports.validation.view',
  'business_reports.validation.decide',
  'business_reports.targets.view',
  'business_reports.targets.create',
  'business_reports.targets.update',
  'business_reports.targets.export',
  'business_reports.target_governance.manage',
  'business_reports.ownership_governance.manage',
  'business_reports.landing_override.request',
  'business_reports.landing_override.approve',
  'business_reports.landing_override.clear',
  'business_reports.export',
  'business_reports.waterfall.view',
  'business_reports.waterfall.export',
  'business_reports.waterfall.source_sensitive.view',
  'business_reports.waterfall.source_sensitive.export',
  'business_reports.daily_revenue.view',
  'business_reports.medics_revenue.view',
  'business_reports.reconciliation.view',
  'business_reports.operations.view',
  'business_reports.operations.retry',
  'business_reports.integrity.view',
  'business_reports.retention.execute',
  'business_reports.source_sensitive.view',
  'business_reports.source_sensitive.export',
]);

export const ROLE_PERMISSIONS = {
  ADMIN: PERMISSIONS.filter((permission) => !ADMIN_EXCLUDED_PERMISSIONS.has(permission)),
  REPORTS: [
    'dashboard.read',
    'dashboard.export',
    'clinical_dashboard.read',
    'clinical_dashboard.export',
    'doctor_report.read',
    'doctor_report.export',
    'daily_clinical_report.read',
    'daily_clinical_report.export',
    'feedback_tracker.read',
    'feedback_tracker.manual',
    'feedback_tracker.send_message',
    'feedback_tracker.export',
  'offer_campaigns.view',
  'offer_campaigns.display_board',
    'feedback_responses.read',
    'feedback_responses.export',
    'question_wise_response.read',
    'question_wise_response.export',
    'monthly_summary.read',
    'monthly_summary.export',
    'detailed_summary.read',
    'detailed_summary.export',
    'exports.read',
    'exports.download',
  ],
  SURVEY: ['survey_app.use', 'survey_questions.read', 'staff_management.read'],
} as const satisfies Record<UserRole, readonly Permission[]>;

const LEGACY_PERMISSION_ALIASES: Record<string, Permission> = {
  'admin:access': 'admin_app.access',
  'users:manage': 'user_accounts.update',
  'reports:read': 'dashboard.read',
  'survey:manage': 'survey_questions.update',
  'survey:submit': 'survey_app.use',
  'feedback:delete': 'feedback_responses.delete',
} as const satisfies Record<string, Permission>;

export function normalizePermission(permission: string): Permission | null {
  if (isPermission(permission)) return permission;
  return LEGACY_PERMISSION_ALIASES[permission] ?? null;
}

export function isPermission(permission: string): permission is Permission {
  return (PERMISSIONS as readonly string[]).includes(permission);
}

export function permissionsForRole(role: UserRole | string | null | undefined): readonly Permission[] {
  if (role === 'ADMIN' || role === 'REPORTS' || role === 'SURVEY') return ROLE_PERMISSIONS[role];
  return [];
}

export function resolveUserPermissions(user: { is_superadmin?: boolean | null; role?: UserRole | string | null } | null | undefined): Permission[] {
  if (!user) return [];
  if (user.is_superadmin) return [...PERMISSIONS];
  return [...permissionsForRole(user.role)];
}

export function roleHasPermission(role: UserRole | string | null | undefined, permission: Permission | string): boolean {
  const normalized = normalizePermission(permission);
  if (!normalized) return false;
  return permissionsForRole(role).includes(normalized);
}

export function userHasPermission(
  user: { is_superadmin?: boolean | null; role?: UserRole | string | null; permissions?: readonly string[] | null } | null | undefined,
  permission: Permission | string,
): boolean {
  const normalized = normalizePermission(permission);
  if (!normalized || !user) return false;
  if (hasDirectPermission(user, normalized)) return true;
  return (PERMISSION_IMPLIED_BY[normalized] || []).some((umbrella) => hasDirectPermission(user, umbrella));
}

const PERMISSION_IMPLIED_BY: Partial<Record<Permission, readonly Permission[]>> = {
  'business_reports.manual_revenue.view': ['business_reports.manage', 'business_reports.manual_revenue.manage', 'business_reports.approve_manual_entries'],
  'business_reports.manual_revenue.manage': ['business_reports.manage'],
  'business_reports.validation.view': ['business_reports.manage', 'business_reports.validation.decide'],
  'business_reports.validation.decide': ['business_reports.manage'],
  'business_reports.targets.view': ['business_reports.manage', 'business_reports.targets.create', 'business_reports.targets.update', 'business_reports.targets.export', 'business_reports.target_governance.manage'],
  'business_reports.targets.create': ['business_reports.manage'],
  'business_reports.targets.update': ['business_reports.manage'],
  'business_reports.targets.export': ['business_reports.manage'],
  'business_reports.target_governance.manage': ['business_reports.manage'],
  'business_reports.ownership_governance.manage': ['business_reports.manage'],
};

function hasDirectPermission(
  user: { is_superadmin?: boolean | null; role?: UserRole | string | null; permissions?: readonly string[] | null },
  permission: Permission,
): boolean {
  if (user.is_superadmin) return true;
  // A hydrated permission list is authoritative. Falling back to the legacy
  // account role here would re-grant every REPORTS page to users whose modern
  // access-control role intentionally contains only module permissions.
  if (Array.isArray(user.permissions)) {
    return user.permissions.some((candidate) => normalizePermission(candidate) === permission);
  }
  return roleHasPermission(user.role, permission);
}
